import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Search, Check, Copy, Sparkles, Trash2, Bookmark } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { tr } from "@/lib/i18n";
import api from "@/lib/api";
import { toast } from "sonner";

/** Dialog with searchable KODEKS + optional custom goal input + personal library + Leo suggestion. */
export function NeedsDialog({ open, onOpenChange, onPick, currentPl, mode = "list", sceneId = null }) {
  const { lang } = useAuth();
  const t = (k) => tr(k, lang);
  const [items, setItems] = useState([]);
  const [customs, setCustoms] = useState([]);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(currentPl || null);
  const [customMode, setCustomMode] = useState(false);
  const [customText, setCustomText] = useState("");
  const [saveToLibrary, setSaveToLibrary] = useState(true);
  const [saving, setSaving] = useState(false);

  // Leo suggestion mode — only for "pick" mode with a sceneId
  const canUseLeo = mode === "pick" && !!sceneId;
  const [leoMode, setLeoMode] = useState(false);
  const [leoBusy, setLeoBusy] = useState(false);
  const [leoSuggestion, setLeoSuggestion] = useState(null); // { cel, uzasadnienie }
  const [leoEditText, setLeoEditText] = useState("");
  const [leoAvoid, setLeoAvoid] = useState([]); // suggestions already rejected/regenerated

  useEffect(() => {
    if (!open) return;
    setSelected(currentPl || null);
    setQ("");
    setCustomMode(false);
    setCustomText("");
    setSaveToLibrary(true);
    setLeoMode(false);
    setLeoBusy(false);
    setLeoSuggestion(null);
    setLeoEditText("");
    setLeoAvoid([]);
    (async () => {
      try {
        const [k, c] = await Promise.all([
          api.get("/kodeks"),
          api.get("/user/custom-needs").catch(() => ({ data: { items: [] } })),
        ]);
        setItems(k.data.items || []);
        setCustoms(c.data.items || []);
      } catch { /* ignore */ }
    })();
  }, [open, currentPl]);

  const askLeo = async () => {
    if (!sceneId || leoBusy) return;
    setLeoBusy(true);
    try {
      const r = await api.post(`/scenes/${sceneId}/goal/suggest`, { avoid: leoAvoid });
      const s = { cel: r.data.cel || "", uzasadnienie: r.data.uzasadnienie || "" };
      setLeoSuggestion(s);
      setLeoEditText(s.cel);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("goal.leo_fail"));
    } finally {
      setLeoBusy(false);
    }
  };

  const requestAnother = async () => {
    if (leoSuggestion?.cel) setLeoAvoid(a => [...a, leoSuggestion.cel]);
    setLeoSuggestion(null);
    setLeoEditText("");
    await askLeo();
  };

  const rejectLeo = () => {
    // switch to custom mode with an empty box so the actor writes their own
    setLeoMode(false);
    setLeoSuggestion(null);
    setLeoEditText("");
    setCustomMode(true);
    setCustomText("");
  };

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return items;
    return items.filter(it => it.pl.toLowerCase().includes(s) || (it.en || "").toLowerCase().includes(s));
  }, [items, q]);

  const filteredCustoms = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return customs;
    return customs.filter(txt => (txt || "").toLowerCase().includes(s));
  }, [customs, q]);

  const copy = async () => {
    try {
      // Copy ONLY the current-language column — never mix PL and EN
      const text = items
        .map((it, i) => {
          const label = lang === "pl" ? it.pl : (it.en || it.pl);
          return `${String(i + 1).padStart(2, "0")}. ${label}`;
        })
        .join("\n");
      await navigator.clipboard.writeText(text);
      toast.success(t("needs.copied"));
    } catch {}
  };

  const confirm = async () => {
    // Leo mode: use the (possibly edited) Leo suggestion as a custom goal
    let goal, isCustom;
    if (leoMode) {
      goal = (leoEditText || "").trim();
      isCustom = true;
    } else if (customMode) {
      goal = (customText || "").trim();
      isCustom = true;
    } else {
      goal = selected || "";
      isCustom = false;
    }
    if (!goal) return;
    if (saving) return;
    setSaving(true);
    try {
      // parent handles the actual /goal call — pass save flag through onPick
      const save = (leoMode || customMode) ? saveToLibrary : false;
      await onPick?.(goal, isCustom, save);
    } finally {
      setSaving(false);
    }
  };

  const removeCustom = async (text) => {
    try {
      const r = await api.delete(`/user/custom-needs?text=${encodeURIComponent(text)}`);
      setCustoms(r.data.items || []);
      if (selected === text) setSelected(null);
    } catch { toast.error(lang === "pl" ? "Nie udało się usunąć." : "Delete failed."); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="needs-dialog" className="bg-stone-950 border-stone-800 text-stone-200 max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="serif text-2xl text-stone-100">
            {mode === "pick" ? t("goal.dialog_title") : t("needs.dialog_title")}
          </DialogTitle>
          <DialogDescription className="text-stone-400">
            {mode === "pick" ? t("goal.dialog_desc") : t("needs.dialog_desc")}
          </DialogDescription>
        </DialogHeader>

        {mode === "pick" && (
          <div className="flex flex-col gap-2 mt-2">
            <div className="flex items-center gap-3 border border-stone-800 rounded-sm p-3 bg-stone-900/50">
              <input
                data-testid="custom-goal-toggle"
                type="checkbox"
                checked={customMode}
                onChange={(e)=> { setCustomMode(e.target.checked); if (e.target.checked) setLeoMode(false); }}
                className="accent-amber-500"
                id="custom-goal-toggle-cb"
              />
              <label htmlFor="custom-goal-toggle-cb" className="text-sm text-stone-200 cursor-pointer flex items-center gap-2">
                <Sparkles size={14} className="text-amber-500"/>
                {t("goal.custom_toggle")}
              </label>
            </div>
            {canUseLeo && (
              <div className="flex items-center gap-3 border border-amber-500/25 rounded-sm p-3 bg-amber-500/[0.04]">
                <input
                  data-testid="leo-goal-toggle"
                  type="checkbox"
                  checked={leoMode}
                  onChange={(e)=> { setLeoMode(e.target.checked); if (e.target.checked) setCustomMode(false); }}
                  className="accent-amber-500"
                  id="leo-goal-toggle-cb"
                />
                <label htmlFor="leo-goal-toggle-cb" className="text-sm text-stone-200 cursor-pointer flex items-center gap-2">
                  <Sparkles size={14} className="text-amber-400"/>
                  {t("goal.leo_toggle")}
                </label>
              </div>
            )}
          </div>
        )}

        {leoMode ? (
          <div className="mt-3 flex-1 flex flex-col gap-3" data-testid="leo-suggest-panel">
            <p className="text-xs text-stone-400 leading-relaxed">{t("goal.leo_intro")}</p>

            {!leoSuggestion && !leoBusy && (
              <button
                data-testid="leo-goal-ask-btn"
                onClick={askLeo}
                className="pill-btn self-start"
              >
                <Sparkles size={14}/> {t("goal.leo_ask")}
              </button>
            )}

            {leoBusy && (
              <div className="mono text-xs text-amber-400/90 flex items-center gap-2" data-testid="leo-goal-thinking">
                <span className="inline-block w-2 h-2 rounded-full bg-amber-500 animate-pulse"/>
                {t("goal.leo_thinking")}
              </div>
            )}

            {leoSuggestion && !leoBusy && (
              <>
                <textarea
                  data-testid="leo-goal-edit"
                  value={leoEditText}
                  onChange={(e)=>setLeoEditText(e.target.value)}
                  rows={3}
                  className="script-input serif text-lg"
                />
                {leoSuggestion.uzasadnienie && (
                  <div className="mono text-[11px] text-stone-500 leading-relaxed">
                    <span className="uppercase tracking-widest text-amber-500/70">{t("goal.leo_reason")}</span>{" "}
                    <span className="text-stone-400">{leoSuggestion.uzasadnienie}</span>
                  </div>
                )}
                <div className="flex flex-wrap gap-2">
                  <button
                    data-testid="leo-goal-again-btn"
                    onClick={requestAnother}
                    className="ghost-btn"
                    disabled={leoBusy}
                  >
                    <Sparkles size={14}/> {t("goal.leo_again")}
                  </button>
                  <button
                    data-testid="leo-goal-reject-btn"
                    onClick={rejectLeo}
                    className="ghost-btn"
                  >
                    {t("goal.leo_reject")}
                  </button>
                </div>

                {/* Save to personal library (only makes sense if custom goal will be saved) */}
                <div className="mt-1 flex items-start gap-3 border border-amber-500/20 rounded-sm p-3 bg-amber-500/[0.03]">
                  <input
                    data-testid="leo-goal-save-library"
                    id="leo-save-lib-cb"
                    type="checkbox"
                    checked={saveToLibrary}
                    onChange={(e)=>setSaveToLibrary(e.target.checked)}
                    className="accent-amber-500 mt-0.5"
                  />
                  <label htmlFor="leo-save-lib-cb" className="cursor-pointer">
                    <span className="text-sm text-stone-100 flex items-center gap-2">
                      <Bookmark size={13} className="text-amber-500"/> {t("goal.save_to_library")}
                    </span>
                    <span className="mono text-[11px] text-stone-500 mt-1 block">{t("goal.save_to_library_hint")}</span>
                  </label>
                </div>
              </>
            )}
          </div>
        ) : customMode ? (
          <div className="mt-3 flex-1">
            <textarea
              data-testid="custom-goal-input"
              value={customText}
              onChange={(e)=>setCustomText(e.target.value)}
              rows={4}
              className="script-input mono text-sm"
              placeholder={t("goal.custom_ph")}
            />
            <p className="mt-2 text-xs text-stone-500 leading-relaxed">{t("goal.custom_hint")}</p>

            <div className="mt-3 flex items-start gap-3 border border-amber-500/30 rounded-sm p-3 bg-amber-500/[0.04]">
              <input
                data-testid="save-to-library-toggle"
                id="save-to-library-cb"
                type="checkbox"
                checked={saveToLibrary}
                onChange={(e)=>setSaveToLibrary(e.target.checked)}
                className="accent-amber-500 mt-0.5"
              />
              <label htmlFor="save-to-library-cb" className="cursor-pointer">
                <span className="text-sm text-stone-100 flex items-center gap-2">
                  <Bookmark size={13} className="text-amber-500"/> {t("goal.save_to_library")}
                </span>
                <span className="mono text-[11px] text-stone-500 mt-1 block">{t("goal.save_to_library_hint")}</span>
              </label>
            </div>
          </div>
        ) : (
          <>
            <div className="relative mt-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-500" size={14}/>
              <input
                data-testid="needs-search"
                value={q}
                onChange={(e)=>setQ(e.target.value)}
                placeholder={t("goal.search_ph")}
                className="script-input pl-9"
              />
            </div>

            <div className="flex-1 overflow-y-auto -mx-2 px-2">
              {/* Personal library first — visible in both pick and list modes */}
              <div className="mt-3">
                <div className="mono text-[10px] tracking-[0.3em] uppercase text-amber-500/80 mb-1.5 flex items-center gap-1.5">
                  <Bookmark size={11}/> {t("needs.your_library")}
                </div>
                {filteredCustoms.length === 0 ? (
                  <p className="text-stone-500 text-xs mono py-2" data-testid="needs-library-empty">{t("needs.your_library_empty")}</p>
                ) : (
                  <ul className="space-y-1">
                    {filteredCustoms.map((txt) => {
                      const isSel = selected === txt;
                      const isCurrent = currentPl === txt;
                      return (
                        <li key={`c:${txt}`} className="flex items-stretch gap-1">
                          <button
                            data-testid={`custom-need-item`}
                            onClick={() => mode === "pick" && setSelected(txt)}
                            className={`flex-1 text-left px-3 py-2 rounded-sm border transition-colors ${
                              isSel ? "border-amber-500 bg-amber-500/10" :
                              isCurrent ? "border-amber-500/40" : "border-stone-800 hover:bg-stone-900"
                            } ${mode !== "pick" ? "cursor-default" : ""}`}
                          >
                            <div className="flex items-baseline justify-between gap-3">
                              <div>
                                <div className="serif text-base text-stone-100">{txt}</div>
                                <div className="mono text-[10px] text-amber-500/60 uppercase tracking-widest">{t("needs.your_library")}</div>
                              </div>
                              {isSel && <Check size={14} className="text-amber-500 shrink-0"/>}
                            </div>
                          </button>
                          <button
                            data-testid={`custom-need-delete`}
                            onClick={() => removeCustom(txt)}
                            title={t("needs.delete_own")}
                            aria-label={t("needs.delete_own")}
                            className="px-2 border border-stone-800 rounded-sm text-stone-500 hover:text-rose-400 hover:border-rose-500/60"
                          >
                            <Trash2 size={12}/>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>

              <div className="mono text-[10px] tracking-[0.3em] uppercase text-stone-500 mt-5 mb-1.5">Kodeks · {items.length}</div>
              <ul className="space-y-1">
                {filtered.map((it) => {
                  // Canonical id stays Polish (it.pl); only the LABEL follows the app language.
                  const isSel = selected === it.pl;
                  const isCurrent = currentPl === it.pl;
                  const label = lang === "pl" ? it.pl : (it.en || it.pl);
                  return (
                    <li key={it.pl}>
                      <button
                        data-testid={`need-item-${it.pl.replace(/\s+/g, "_")}`}
                        onClick={() => mode === "pick" && setSelected(it.pl)}
                        className={`w-full text-left px-3 py-2 rounded-sm border transition-colors ${
                          isSel ? "border-amber-500 bg-amber-500/10" :
                          isCurrent ? "border-amber-500/40" : "border-stone-800 hover:bg-stone-900"
                        } ${mode !== "pick" ? "cursor-default" : ""}`}
                      >
                        <div className="flex items-baseline justify-between gap-3">
                          <div>
                            <div className="serif text-lg text-stone-100">{label}</div>
                          </div>
                          {isSel && <Check size={16} className="text-amber-500 shrink-0"/>}
                          {isCurrent && !isSel && <span className="mono text-[10px] text-amber-500/70 uppercase tracking-widest">current</span>}
                        </div>
                      </button>
                    </li>
                  );
                })}
                {filtered.length === 0 && (
                  <li className="text-center text-stone-500 py-6 mono text-xs">— {lang === "pl" ? "nic nie znaleziono" : "no results"} —</li>
                )}
              </ul>
            </div>
          </>
        )}

        <DialogFooter className="pt-3 border-t border-stone-800">
          {mode === "pick" ? (
            <>
              <button className="ghost-btn" disabled={saving} onClick={() => onOpenChange(false)}>{t("action.cancel")}</button>
              <button
                data-testid="needs-confirm-btn"
                className="pill-btn"
                disabled={saving || (leoMode ? !leoEditText.trim() : (customMode ? !customText.trim() : !selected))}
                onClick={confirm}
              >
                {saving ? t("goal.regenerating") : (leoMode ? t("goal.leo_accept") : t("goal.confirm"))}
              </button>
            </>
          ) : (
            <>
              <button data-testid="needs-copy-btn" className="ghost-btn" onClick={copy}><Copy size={14}/> {t("needs.copy")}</button>
              <button className="pill-btn" onClick={() => onOpenChange(false)}>{t("action.close")}</button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
